import { memo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import type { SelectionDropdownItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import QuotaTotalCost from "app/pages/sales-intelligence/modules/quota/components/QuotaTotalCost";

import { StyledDropdownItem } from "./styles";

interface SelectionDDItemProps {
    item: SelectionDropdownItem;
    isSelected: boolean;
    getOperationCost: (count: number) => number;
    canAffordOperation: (cost: number) => boolean;
    onClick?: (item: SelectionDropdownItem) => void;
}

const SelectionDDQuotaItem = (props: SelectionDDItemProps) => {
    const { item, isSelected, getOperationCost, canAffordOperation, onClick } = props;

    const { translate } = useTranslate();

    const isDisabled = typeof item.disabledReason !== "undefined";
    const translation = item.getTranslation();

    const operationCost = getOperationCost(item.value);

    const handleClick = () => {
        if (isDisabled) {
            return;
        }

        onClick?.(item);
    };

    const renderedItem = (
        <StyledDropdownItem
            isSelected={isSelected}
            isDisabled={isDisabled}
            onClick={handleClick}
            data-automation="selection-dropdown-item"
            data-automation-id={item.id}
            data-automation-disabled={isDisabled}
            data-automation-selected={isSelected}
        >
            <Text
                data-automation="selection-dropdown-item.text"
                fontVariant="report"
                color={colorsPalettes.carbon[500]}
                lineHeight="44px"
            >
                {translate(translation.key, translation.replacements)}
            </Text>

            <QuotaTotalCost
                isOutOfQuota={!canAffordOperation(operationCost)}
                totalCost={operationCost}
            />
        </StyledDropdownItem>
    );

    if (isDisabled) {
        return (
            <Tooltip content={<I18n translatable={item.disabledReason} />}>
                <div>{renderedItem}</div>
            </Tooltip>
        );
    }

    return renderedItem;
};

export default memo(SelectionDDQuotaItem);
